const STORAGE_KEY = 'ai-chat.validated-links';

type LinkValidationCache = Record<string, boolean>;

const inflight = new Map<string, Promise<boolean>>();

function readCache(): LinkValidationCache {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);

        if (!raw) {
            return {};
        }

        const parsed = JSON.parse(raw) as unknown;

        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return {};
        }

        return parsed as LinkValidationCache;
    } catch {
        return {};
    }
}

function writeCache(url: string, ok: boolean): void {
    const cache = readCache();
    cache[url] = ok;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
}

/**
 * Нормализует маршрут агента: относительные пути получают префикс `/core`.
 */
export function normalizeAgentLinkRoute(route: string): string | null {
    const trimmed = route.trim();

    if (!trimmed) {
        return null;
    }

    if (/^https?:\/\//i.test(trimmed)) {
        return trimmed;
    }

    const withSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;

    if (withSlash === '/core' || withSlash.startsWith('/core/')) {
        return withSlash;
    }

    return `/core${withSlash}`;
}

/**
 * Возвращает закэшированный результат проверки ссылки.
 * `null` — URL ещё не проверяли в этой вкладке.
 */
export function getCachedAgentLinkStatus(route: string): boolean | null {
    const url = normalizeAgentLinkRoute(route);

    if (!url) {
        return false;
    }

    const cache = readCache();

    return Object.prototype.hasOwnProperty.call(cache, url) ? cache[url] : null;
}

/**
 * Проверяет доступность ссылки агента через GET.
 * Результат кэшируется в sessionStorage, чтобы не дергать один URL повторно.
 */
export async function isAgentLinkReachable(route: string): Promise<boolean> {
    const url = normalizeAgentLinkRoute(route);

    if (!url) {
        return false;
    }

    const cached = getCachedAgentLinkStatus(route);

    if (cached !== null) {
        return cached;
    }

    const pending = inflight.get(url);

    if (pending) {
        return pending;
    }

    const request = (async (): Promise<boolean> => {
        try {
            const response = await fetch(url, {
                method: 'GET',
                credentials: 'same-origin',
                redirect: 'follow',
            });
            const ok = response.status >= 200 && response.status < 300;

            if (!ok) {
                console.warn(`Agent link validation failed: status=${response.status}, url=${url}`);
            }

            writeCache(url, ok);
            return ok;
        } catch (error) {
            console.warn(`Agent link validation failed: network error, url=${url}`, error);
            writeCache(url, false);
            return false;
        } finally {
            inflight.delete(url);
        }
    })();

    inflight.set(url, request);
    return request;
}
